// Baner zgody na cookies analityczne (Google Analytics), zgodny z Google Consent Mode v2.
// Domyślna zgoda ("denied") jest ustawiana inline w base.html, zanim wczyta się gtag.js -
// ten skrypt tylko odczytuje/zapisuje decyzję użytkownika i aktualizuje zgodę przez gtag,
// gdy zapadnie decyzja lub gdy wracająca osoba już wcześniej zgodę wyraziła.
//
// Względy UX oparte na badaniach nad bannerami zgody (Utz i in. 2019 "(Un)informed Consent";
// Nouwens i in. 2020 "Dark Patterns after GDPR" - to badanie, które ujawniło praktyki ukarane
// później przez CNIL u Google/Facebook) - świadomie NIE stosujemy tu żadnej z opisanych tam
// manipulacji, bo są niezgodne z prawem, a i tak nie poprawiają jakości zgody:
// - Odrzuć i Akceptuj mają identyczny rozmiar, styl przycisku i liczbę kliknięć (1) - Nouwens
//   pokazał, że dodatkowy krok do odrzucenia (np. chowanie go w "Ustawienia") sztucznie zawyża
//   zgody nawet o kilkadziesiąt punktów proc., ale to zgoda nieważna prawnie.
// - Brak domyślnie zaznaczonych opcji i brak "zgody przez przewinięcie/zamknięcie strony"
//   (wyrok TSUE Planet49 wprost tego zakazuje).
// - Neutralny, rzeczowy tekst bez straszenia ("serwis może nie działać") - Utz pokazał, że
//   język nastawiony na presję podnosi zgody kosztem tego, że nie są one świadome.
// Jedyny celowy zabieg zwiększający *zauważalność* (a nie stronniczość wyboru) to płynne
// wjechanie baneru - przeciwdziała "banner blindness" bez faworyzowania żadnej z opcji.

declare global {
    interface Window {
        dataLayer: unknown[];
        gtag: (...args: unknown[]) => void;
        MUMIN_GA_MEASUREMENT_ID?: string;
    }
}

const CONSENT_KEY = "mumin_cookie_consent";
type ConsentValue = "granted" | "denied";

function getStoredConsent(): ConsentValue | null {
    const value = localStorage.getItem(CONSENT_KEY);
    return value === "granted" || value === "denied" ? value : null;
}

// Zgłasza decyzję do serwerowego licznika (DailyConsentDecisionCount, patrz models.py) - RAZ na
// przeglądarkę na dzień (nie przy każdej odsłonie), inaczej licznik liczyłby odsłony zamiast
// osób i nie dałoby się go sensownie zestawić z unique_count na dashboardzie admina. Wywoływane
// też dla decyzji podjętej wcześniej (patrz niżej), żeby dashboard pokazywał pełny, bieżący
// rozkład zgód wśród odwiedzających danego dnia, nie tylko świeżo kliknięte decyzje.
const CONSENT_REPORT_KEY = "mumin_cookie_consent_reported_date";

function reportDecisionOnce(value: ConsentValue): void {
    const today = new Date().toISOString().slice(0, 10);
    if (localStorage.getItem(CONSENT_REPORT_KEY) === today) return;
    localStorage.setItem(CONSENT_REPORT_KEY, today);
    const csrfToken = document.cookie.match(/csrftoken=([^;]+)/)?.[1] ?? "";
    fetch("/api/consent/decision/", {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-CSRFToken": csrfToken },
        body: JSON.stringify({ decision: value }),
    }).catch(() => { /* licznik jest tylko poglądowy - błąd sieci nie ma wpływu na UX */ });
}

// Do momentu zgody base.html ładuje tylko lekki stub gtag() (kolejkujący wywołania do
// dataLayer), NIE prawdziwy skrypt Google (gtag/js) - sama jego obecność to już połączenie
// sieciowe do serwera Google (IP, user-agent), niezależnie od tego, czy Consent Mode zablokuje
// zapis ciasteczek. loadGtagScript() dokleja ten skrypt do <head> dopiero w momencie zgody
// (albo od razu przy powrocie kogoś, kto już wcześniej zaakceptował) - dataLayer trzyma w
// międzyczasie kolejkę (w tym 'consent'/'default' i 'config' wywołane w base.html), więc nic
// się nie gubi, gdy realny gtag.js w końcu dojedzie i ją przetworzy.
let gtagScriptLoaded = false;

function loadGtagScript(): void {
    if (gtagScriptLoaded || !window.MUMIN_GA_MEASUREMENT_ID) {
        return;
    }
    gtagScriptLoaded = true;
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${window.MUMIN_GA_MEASUREMENT_ID}`;
    document.head.appendChild(script);
}

function updateConsent(value: ConsentValue): void {
    if (value === "granted") {
        loadGtagScript();
    }
    window.gtag("consent", "update", {
        analytics_storage: value,
    });
}

function renderBanner(): void {
    const banner = document.createElement("div");
    banner.id = "cookie-consent-banner";
    banner.className = "cookie-consent-banner";
    banner.setAttribute("role", "dialog");
    banner.setAttribute("aria-label", "Zgoda na pliki cookies");

    const text = document.createElement("p");
    text.className = "cookie-consent-text";
    text.append("Używamy cookies analitycznych (Google Analytics). ");
    const link = document.createElement("a");
    link.href = "/rules";
    link.textContent = "Polityka prywatności";
    text.append(link, ".");

    const actions = document.createElement("div");
    actions.className = "cookie-consent-actions";

    const rejectBtn = document.createElement("button");
    rejectBtn.type = "button";
    rejectBtn.className = "cookie-consent-btn cookie-consent-btn-secondary";
    rejectBtn.textContent = "Odrzuć";
    rejectBtn.addEventListener("click", () => {
        localStorage.setItem(CONSENT_KEY, "denied");
        // Jawne wywołanie (nie tylko zapis w localStorage) - istotne przy PONOWNYM otwarciu
        // baneru z menu (patrz openBanner niżej): jeśli ktoś wcześniej zaakceptował, gtag.js
        // jest już wczytany i realnie zbiera dane - samo nadpisanie localStorage tego by nie
        // zatrzymało, dopóki nie przyjdzie kolejne 'consent'/'update' z wartością "denied".
        updateConsent("denied");
        reportDecisionOnce("denied");
        banner.remove();
    });

    const acceptBtn = document.createElement("button");
    acceptBtn.type = "button";
    acceptBtn.className = "cookie-consent-btn cookie-consent-btn-primary";
    acceptBtn.textContent = "Akceptuj";
    acceptBtn.addEventListener("click", () => {
        localStorage.setItem(CONSENT_KEY, "granted");
        updateConsent("granted");
        reportDecisionOnce("granted");
        banner.remove();
    });

    actions.append(rejectBtn, acceptBtn);
    banner.append(text, actions);
    document.body.appendChild(banner);

    // Wjazd z lekkim opóźnieniem (rAF) - baner nie "przeskakuje" bezpośrednio z display:none
    // do widocznego stanu, tylko wjeżdża, co realnie poprawia zauważalność (patrz komentarz
    // u góry pliku), a nie tylko estetykę.
    requestAnimationFrame(() => {
        requestAnimationFrame(() => {
            banner.classList.add("cookie-consent-banner-visible");
        });
    });
}

// Otwiera baner ponownie niezależnie od wcześniejszej decyzji - wywoływane z przycisku
// "Ustawienia cookies" w menu nagłówka (patrz base.html/siteHeaderMenu.ts), jedynej ścieżki do
// zmiany zdania poza ręcznym czyszczeniem danych strony w przeglądarce (patrz rules.html §5.3).
// Strażnik przed duplikatem - button można kliknąć wielokrotnie, zanim ktoś podejmie decyzję.
function openBanner(): void {
    if (document.getElementById("cookie-consent-banner")) return;
    renderBanner();
}

const stored = getStoredConsent();
if (stored === "granted") {
    updateConsent("granted");
    reportDecisionOnce("granted");
} else if (stored === "denied") {
    // Domyślna zgoda już jest "denied" (patrz inline skrypt w base.html), więc nic nie trzeba
    // aktualizować w gtag - ale wciąż zgłaszamy do licznika, żeby dashboard admina widział
    // PEŁNY, bieżący rozkład decyzji wśród dzisiejszych odwiedzających, nie tylko świeże kliknięcia.
    reportDecisionOnce("denied");
} else {
    renderBanner();
}

const cookieSettingsBtn = document.getElementById("site-cookie-settings");
if (cookieSettingsBtn) {
    cookieSettingsBtn.addEventListener("click", (event) => {
        event.preventDefault();
        openBanner();
    });
}

export {};
